import { useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useToast } from '../../../../../../modules/components/toast/ToastContext';
import {
  CategorizeServiceSection,
  FieldStatus,
  keywordLabel,
  KeywordType,
  SiService,
  siteIntelligenceServiceLabel,
} from '../../../../mappings';
import KeywordTypeBadge from '../../../../../../modules/components/badge/KeywordTypeBadge';
import SexualContent from './SexualContent';
import TobaccoContent from './TobaccoContent';
import CategorizeContent from './CategorizeContent';
import ImageListModal from '../../../../../../modules/components/modal/ImageListModal';
import MembersOnlyContent from './MembersOnlyContent';
import { AddCategoryModal } from '../../modal/AddCategoryModal';
import EditableCardContainer from '../../card/EditableCardContainer';
import { HistoryModal } from '../../modal/HistoryModal';
import type { Request, UsePageDataManagerResult } from '../../hooks/usePageDataManager';
import { getFieldUpdate, getImagePathByCaptureId, SiRequestUser } from '../../utils';

interface Props {
  data: {
    [SiService.ADULT_SHIELD_SERVICE]: any;
    [SiService.TOBACCO_SIFT_SERVICE]: any;
    [SiService.MEMBERS_ONLY_SIFT_SERVICE]: any;
    [SiService.CATEGORIZE_SERVICE]: any;
  };
  screenshots?: any[];
  isSiAiNewData?: boolean;
  reviewer?: SiRequestUser[];
  status: string;
  translation: { key: string, value: string, language: 'sc' }[];
  currentLanguage: 'sc' | 'en' | 'tc';
  refetch: () => void;
  defaultCaptureId?: string;
  isEditable?: boolean;
  isSaving?: boolean;
  onUpdate?: UsePageDataManagerResult['updateField'];
  onSaveMultiple?: UsePageDataManagerResult['saveMultipleServices'];
  onReset?: UsePageDataManagerResult['resetService'];
  checkDirty?: UsePageDataManagerResult['isServiceDirty'];
}

export const getCardId = (category: SiService | KeywordType) =>
  `${(siteIntelligenceServiceLabel[category] ?? keywordLabel[category] ?? category)
    ?.toLowerCase()
    ?.split(' ')[0]
  }-content`;

export default function CategoryDetail({
  data,
  screenshots,
  isSiAiNewData,
  reviewer,
  status,
  translation,
  currentLanguage,
  refetch,
  defaultCaptureId,
  isEditable,
  isSaving,
  onUpdate,
  onSaveMultiple,
  onReset,
  checkDirty,
}: Props) {
  const intl = useIntl();
  const { setToast } = useToast();
  const t = (id: string) => intl.formatMessage({ id: `SI_CATEGORY_DETAIL.${id}` });

  const [showImagesModal, setShowImagesModal] = useState(false);
  const [selectedImagesData, setSelectedImagesData] =
    useState<{ title?: string; images: string[] }>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showHistoryModal, setShowHistoryModal] = useState(false);

  const adultFieldUpdates = [
    getFieldUpdate('result.adult', data?.[SiService.ADULT_SHIELD_SERVICE]?.field_updates),
    getFieldUpdate('result.categories', data?.[SiService.ADULT_SHIELD_SERVICE]?.field_updates),
  ];
  const tobaccoFieldUpdates = [
    getFieldUpdate('result.tobacco', data?.[SiService.TOBACCO_SIFT_SERVICE]?.field_updates),
    getFieldUpdate('result.cuban', data?.[SiService.TOBACCO_SIFT_SERVICE]?.field_updates),
    getFieldUpdate(
      'result.cuban_cigar_brands',
      data?.[SiService.TOBACCO_SIFT_SERVICE]?.field_updates
    ),
  ];
  const membersOnlyFieldUpdates = [
    getFieldUpdate(
      'result.members_only',
      data?.[SiService.MEMBERS_ONLY_SIFT_SERVICE]?.field_updates
    ),
  ];
  const categorizeFieldUpdates = [
    getFieldUpdate('result.categories', data?.[SiService.CATEGORIZE_SERVICE]?.field_updates),
  ];
  const hasHistory = [
    ...adultFieldUpdates,
    ...tobaccoFieldUpdates,
    ...membersOnlyFieldUpdates,
    ...categorizeFieldUpdates,
  ].some((fieldUpdates) => fieldUpdates.length > 0);

  const categorySectionDetailIndex = data?.[SiService.CATEGORIZE_SERVICE]?.detail?.findIndex(
    (d) => d?.section === CategorizeServiceSection.CATEGORY
  );
  const defaultScreenshotUrl = getImagePathByCaptureId(defaultCaptureId, screenshots);

  type CategoryStatusMap = Map<
    SiService | KeywordType,
    {
      isHidden: boolean;
    }
  >;
  const { detectedCategoryStatusMap, dirtyServiceMap } = useMemo(() => {
    const dirtyServiceMap: Map<SiService, boolean> = new Map();
    const detectedCategoryStatusMap: CategoryStatusMap = new Map();
    if (!data) return { detectedCategoryStatusMap, dirtyServiceMap };

    if (checkDirty) {
      dirtyServiceMap
        .set(SiService.ADULT_SHIELD_SERVICE, checkDirty(SiService.ADULT_SHIELD_SERVICE))
        .set(SiService.TOBACCO_SIFT_SERVICE, checkDirty(SiService.TOBACCO_SIFT_SERVICE))
        .set(SiService.MEMBERS_ONLY_SIFT_SERVICE, checkDirty(SiService.MEMBERS_ONLY_SIFT_SERVICE))
        .set(
          SiService.CATEGORIZE_SERVICE,
          data?.[SiService.CATEGORIZE_SERVICE] &&
          checkDirty(SiService.CATEGORIZE_SERVICE, [
            `detail[${categorySectionDetailIndex}]`,
            'result.categories',
          ])
        );
    }

    const hasActive = (cats: any[]) => cats?.some((c) => c?.field_status === FieldStatus.ACTIVE);

    const sexualResult = data?.[SiService.ADULT_SHIELD_SERVICE]?.result;
    if (sexualResult?.adult || sexualResult?.categories?.length > 0) {
      detectedCategoryStatusMap.set(SiService.ADULT_SHIELD_SERVICE, {
        isHidden:
          !sexualResult?.adult ||
          (sexualResult?.categories?.length > 0 && !hasActive(sexualResult?.categories)),
      });
    }

    const tobaccoResult = data?.[SiService.TOBACCO_SIFT_SERVICE]?.result;
    if (
      tobaccoResult?.tobacco ||
      tobaccoResult?.cuban ||
      tobaccoResult?.cuban_cigar_brands?.length > 0
    ) {
      detectedCategoryStatusMap.set(SiService.TOBACCO_SIFT_SERVICE, {
        isHidden:
          !tobaccoResult?.tobacco ||
          (tobaccoResult?.cuban_cigar_brands?.length > 0 &&
            !hasActive(tobaccoResult?.cuban_cigar_brands)),
      });
    }

    const membersOnlyResult = data?.[SiService.MEMBERS_ONLY_SIFT_SERVICE]?.result;
    // TODO: confirm to always show members only or not
    if (membersOnlyResult?.members_only) {
      detectedCategoryStatusMap.set(SiService.MEMBERS_ONLY_SIFT_SERVICE, {
        isHidden: !membersOnlyResult?.members_only,
      });
    }

    const categorizeResult = data?.[SiService.CATEGORIZE_SERVICE]?.result;
    if (categorizeResult?.categories?.length > 0) {
      for (const c of categorizeResult?.categories) {
        detectedCategoryStatusMap.set(c.category, {
          isHidden: c?.field_status !== FieldStatus.ACTIVE,
        });
      }
    }

    return { detectedCategoryStatusMap, dirtyServiceMap };
  }, [data]);

  const isDirty = Array.from(dirtyServiceMap.values()).some((v) => v);

  const hasActiveCategories = Array.from(detectedCategoryStatusMap.values()).some(
    (v) => !v.isHidden
  );

  const handleExpandImages = (images: string[], title?: string) => {
    setSelectedImagesData({ title, images });
    setShowImagesModal(true);
  };

  const handleOpenAddModal = () => {
    if (isDirty) {
      alert('You have unsaved changes. Please save before proceeding.');
    } else {
      setShowAddModal(true);
    }
  };

  return (
    <>
      <EditableCardContainer
        title={t('TITLE')}
        haveHistory={hasHistory && isEditable}
        onHistory={hasHistory ? () => setShowHistoryModal(true) : undefined}
        headerToolbar={
          isEditable && (
            <div
              className='btn btn-icon btn-sm btn-primary'
              onClick={handleOpenAddModal}
              title='Add Category Details'
            >
              <i className='fas fa-plus'></i>
            </div>
          )
        }
        isEditable={isEditable}
        isDirty={isDirty}
        isSaving={isSaving}
        onSave={() => {
          const requests: Request[] = [];
          Array.from(dirtyServiceMap.entries()).forEach(([service, isDirty]) => {
            if (isDirty) {
              if (service !== SiService.CATEGORIZE_SERVICE) {
                requests.push({ selector: service });
              } else {
                requests.push({
                  selector: service,
                  paths: [`detail[${categorySectionDetailIndex}]`, 'result.categories'],
                });
              }
            }
          });
          if (requests.length > 0) {
            onSaveMultiple?.(requests);
          }
        }}
        onReset={() => {
          // TODO:
          // onReset?.(SiService.CATEGORIZE_SERVICE)
        }}
      >
        <div className='p-9 vstack gap-9'>
          {/* {detectedCategoryStatusMap.size > 0 && (
            <div className='d-flex flex-wrap gap-2 align-items-center'>
              <h5 className='fw-bolder m-0'>Detected Categories: </h5>
              {Array.from(detectedCategoryStatusMap).map(([category, { isHidden }]) =>
                isEditable || !isHidden ? (
                  <a
                    key={category}
                    href={`#${getCardId(category)}`}
                    className='btn btn-flush btn-hover-rise'
                  >
                    <KeywordTypeBadge type={category} className={isHidden && 'opacity-25'} />
                  </a>
                ) : (
                  <></>
                )
              )}
            </div>
          )} */}

          {(detectedCategoryStatusMap.size === 0 || (!isEditable && !hasActiveCategories)) && (
            <div className='text-center'>{t('NO_SENSITIVE_CATEGORY')}</div>
          )}

          {(isEditable || hasActiveCategories || isDirty) && (
            <>
              {(detectedCategoryStatusMap.has(SiService.ADULT_SHIELD_SERVICE) ||
                !!dirtyServiceMap.get(SiService.ADULT_SHIELD_SERVICE)) &&
                (isEditable ||
                  !!dirtyServiceMap.get(SiService.ADULT_SHIELD_SERVICE) ||
                  (detectedCategoryStatusMap.has(SiService.ADULT_SHIELD_SERVICE) &&
                    !detectedCategoryStatusMap.get(SiService.ADULT_SHIELD_SERVICE).isHidden)) && (
                  <SexualContent
                    data={data[SiService.ADULT_SHIELD_SERVICE]}
                    screenshots={screenshots}
                    status={status}
                    translation={translation}
                    currentLanguage={currentLanguage}
                    refetch={refetch}
                    onExpandImages={handleExpandImages}
                    isEditable={isEditable}
                    isSiAiNewData={isSiAiNewData}
                    onUpdate={onUpdate}
                  />
                )}

              {(detectedCategoryStatusMap.has(SiService.TOBACCO_SIFT_SERVICE) ||
                !!dirtyServiceMap.get(SiService.TOBACCO_SIFT_SERVICE)) &&
                (isEditable ||
                  !!dirtyServiceMap.get(SiService.TOBACCO_SIFT_SERVICE) ||
                  (detectedCategoryStatusMap.has(SiService.TOBACCO_SIFT_SERVICE) &&
                    !detectedCategoryStatusMap.get(SiService.TOBACCO_SIFT_SERVICE).isHidden)) && (
                  <TobaccoContent
                    data={data[SiService.TOBACCO_SIFT_SERVICE]}
                    screenshots={screenshots}
                    status={status}
                    translation={translation}
                    currentLanguage={currentLanguage}
                    refetch={refetch}
                    onExpandImages={handleExpandImages}
                    isEditable={isEditable}
                    isSiAiNewData={isSiAiNewData}
                    onUpdate={onUpdate}
                  />
                )}

              {(detectedCategoryStatusMap.has(SiService.MEMBERS_ONLY_SIFT_SERVICE) ||
                !!dirtyServiceMap.get(SiService.MEMBERS_ONLY_SIFT_SERVICE)) &&
                (isEditable ||
                  !!dirtyServiceMap.get(SiService.MEMBERS_ONLY_SIFT_SERVICE) ||
                  (detectedCategoryStatusMap.has(SiService.MEMBERS_ONLY_SIFT_SERVICE) &&
                    !detectedCategoryStatusMap.get(SiService.MEMBERS_ONLY_SIFT_SERVICE).isHidden)) && (
                  <MembersOnlyContent
                    data={data[SiService.MEMBERS_ONLY_SIFT_SERVICE]}
                    status={status}
                    refetch={refetch}
                    isEditable={isEditable}
                    isSiAiNewData={isSiAiNewData}
                    onUpdate={onUpdate}
                  />
                )}

              {data?.[SiService.CATEGORIZE_SERVICE]?.result?.categories?.length > 0 && (
                <CategorizeContent
                  data={data[SiService.CATEGORIZE_SERVICE]}
                  screenshots={screenshots}
                  status={status}
                  translation={translation}
                  currentLanguage={currentLanguage}
                  refetch={refetch}
                  onExpandImages={handleExpandImages}
                  isEditable={isEditable}
                  isSiAiNewData={isSiAiNewData}
                  onUpdate={onUpdate}
                />
              )}
            </>
          )}
        </div>
        <div className='d-flex justify-content-end mb-5'>
          <a href='#summary' className='px-8 d-none print-display-button'>
            <i className="bi bi-caret-up-square-fill fs-3"></i>
          </a>
        </div>
      </EditableCardContainer>

      <ImageListModal
        title={selectedImagesData?.title || t('CAPTURES')}
        imagePaths={selectedImagesData?.images ?? []}
        show={showImagesModal}
        onHide={() => setShowImagesModal(false)}
        restoreFocus={false} // prevent restoring focus to previously focused element on hide
      />

      <AddCategoryModal
        data={data}
        show={showAddModal}
        isSiAiNewData={isSiAiNewData}
        handleClose={() => setShowAddModal(false)}
        handleUpdate={onUpdate}
        handleSaveMultiple={onSaveMultiple}
        defaultScreenshotUrl={defaultScreenshotUrl}
      />

      <HistoryModal
        show={showHistoryModal}
        handleClose={() => setShowHistoryModal(false)}
        data={{ results: { users: reviewer ?? [] } }}
        service_paths={[
          {
            ref_id: data?.[SiService.ADULT_SHIELD_SERVICE]?._id,
            label: siteIntelligenceServiceLabel.ADULT_SHIELD_SERVICE,
            paths: ['result.adult', 'result.categories'],
            pathLabels: {
              'result.adult': 'Restricted Adult / Adult',
              'result.categories': 'Restricted Adult / Categories',
            },
          },
          {
            ref_id: data?.[SiService.TOBACCO_SIFT_SERVICE]?._id,
            label: siteIntelligenceServiceLabel.TOBACCO_SIFT_SERVICE,
            paths: ['result.tobacco', 'result.cuban', 'result.cuban_cigar_brands'],
            pathLabels: {
              'result.tobacco': 'Tobacco / Tobacco',
              'result.cuban': 'Tobacco / Cuban',
              'result.cuban_cigar_brands': 'Tobacco / Cuban Cigar Brands',
            },
          },
          {
            ref_id: data?.[SiService.MEMBERS_ONLY_SIFT_SERVICE]?._id,
            label: siteIntelligenceServiceLabel.MEMBERS_ONLY_SIFT_SERVICE,
            paths: ['result.members_only'],
            pathLabels: {
              'result.members_only': 'Members Only / Members Only',
            },
          },
          {
            ref_id: data?.[SiService.CATEGORIZE_SERVICE]?._id,
            label: siteIntelligenceServiceLabel.CATEGORIZE_SERVICE,
            paths: ['result.categories'],
            pathLabels: {
              'result.categories': 'Category / Categories',
            },
          },
        ]}
      />
    </>
  );
}
